import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { Shield, Filter, RefreshCw, ChevronRight, ChevronDown, Activity, User, Database, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  create: { label: "Oluştur", color: "#16a34a", bg: "#dcfce7" },
  update: { label: "Düzenle", color: "#2563eb", bg: "#dbeafe" },
  delete: { label: "Sil", color: "#dc2626", bg: "#fee2e2" },
};

const ENTITY_LABEL = {
  credit_card: "Kredi Kartı", card_statement: "Kart Ekstresi", daily_expense: "Günlük Harcama",
  transfer: "Transfer", cari: "Cari", trip: "Sefer", trip_cost: "Sefer Masrafı",
  loan: "Kredi", check: "Çek", escrow: "Emanet", bank_account: "Banka Hesabı",
  transaction: "İşlem", obligation: "Ödeme", receivable: "Alacak/Borç",
  user: "Kullanıcı", attachment: "Dosya", finance_cost: "Finans Gideri",
};

export default function AuditLogPage() {
  const [data, setData] = useState({ total: 0, rows: [] });
  const [stats, setStats] = useState(null);
  const [distinct, setDistinct] = useState({ users: [], entities: [] });
  const [filters, setFilters] = useState({ user_email: "", entity_type: "all", action: "all", start: "", end: "" });
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState(false);
  const limit = 100;

  const load = async () => {
    setLoading(true);
    const params = { limit, skip: page * limit };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/audit-logs`, { params }),
        axios.get(`${API}/audit-logs/stats`),
      ]);
      setData(l.data); setStats(s.data); setDenied(false);
    } catch (ex) {
      if (ex.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    axios.get(`${API}/audit-logs/distinct`)
      .then(r => setDistinct(r.data))
      .catch(ex => { if (ex.response?.status === 403) setDenied(true); });
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filters]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const totalPages = Math.ceil(data.total / limit);

  if (denied) {
    return <div className="page">
      <section className="page-toolbar"><div>
        <p className="eyebrow">DENETİM GEÇMİŞİ</p>
        <h2>Erişim engellendi</h2>
      </div></section>
      <section className="panel" style={{ padding: 40, textAlign: "center" }} data-testid="audit-denied">
        <Shield size={48} color="#dc2626" style={{ margin: "0 auto 16px", display: "block" }} />
        <h3 style={{ margin: 0, color: "#dc2626" }}>Bu sayfaya erişiminiz yok</h3>
        <p className="muted" style={{ marginTop: 8 }}>Denetim geçmişi yalnızca yöneticiler tarafından görüntülenebilir.</p>
      </section>
    </div>;
  }

  return <div className="page">
    <section className="page-toolbar">
      <div>
        <p className="eyebrow">DENETİM GEÇMİŞİ</p>
        <h2>{data.total.toLocaleString("tr-TR")} Kayıt{stats && ` · Son 24 Saat ${stats.last_24h}`}</h2>
        <p className="muted">Oluşturma, güncelleme ve silme işlemlerinin kullanıcı bazlı izi. Yalnızca yöneticilere açıktır.</p>
      </div>
      <div className="toolbar-actions">
        <button className="outline-btn" data-testid="audit-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "spin" : ""} /> Yenile
        </button>
      </div>
    </section>

    {stats && <section className="metric-grid" data-testid="audit-stats">
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><Activity size={13} /> Toplam İşlem</span></div>
        <div className="metric-value">{stats.total.toLocaleString("tr-TR")}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label">Son 24 Saat</span></div>
        <div className="metric-value">{stats.last_24h.toLocaleString("tr-TR")}</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><User size={13} /> En Aktif Kullanıcı</span></div>
        <div className="metric-value" style={{ fontSize: 16 }}>{stats.by_user[0]?.user || "—"}</div>
        <div className="muted" style={{ fontSize: 12 }}>{stats.by_user[0]?.count || 0} işlem</div>
      </div>
      <div className="metric-card">
        <div className="metric-head"><span className="metric-label"><Database size={13} /> En Çok Etkilenen</span></div>
        <div className="metric-value" style={{ fontSize: 16 }}>{ENTITY_LABEL[stats.by_entity[0]?.entity] || stats.by_entity[0]?.entity || "—"}</div>
        <div className="muted" style={{ fontSize: 12 }}>{stats.by_entity[0]?.count || 0} işlem</div>
      </div>
    </section>}

    {/* Filters */}
    <section className="panel" style={{ padding: 14, marginBottom: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, alignItems: "end" }}>
        <label className="field"><span><Filter size={11} /> Kullanıcı</span>
          <select data-testid="audit-filter-user" value={filters.user_email} onChange={e => { setPage(0); setFilters({ ...filters, user_email: e.target.value }); }}>
            <option value="">Tüm kullanıcılar</option>
            {distinct.users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </label>
        <label className="field"><span>Kayıt tipi</span>
          <select data-testid="audit-filter-entity" value={filters.entity_type} onChange={e => { setPage(0); setFilters({ ...filters, entity_type: e.target.value }); }}>
            <option value="all">Hepsi</option>
            {distinct.entities.map(e => <option key={e} value={e}>{ENTITY_LABEL[e] || e}</option>)}
          </select>
        </label>
        <label className="field"><span>İşlem</span>
          <select data-testid="audit-filter-action" value={filters.action} onChange={e => { setPage(0); setFilters({ ...filters, action: e.target.value }); }}>
            <option value="all">Hepsi</option>
            <option value="create">Oluşturma</option>
            <option value="update">Düzenleme</option>
            <option value="delete">Silme</option>
          </select>
        </label>
        <label className="field"><span>Başlangıç</span><input data-testid="audit-filter-start" type="date" value={filters.start} onChange={e => { setPage(0); setFilters({ ...filters, start: e.target.value }); }} /></label>
        <label className="field"><span>Bitiş</span><input data-testid="audit-filter-end" type="date" value={filters.end} onChange={e => { setPage(0); setFilters({ ...filters, end: e.target.value }); }} /></label>
        <button className="outline-btn" data-testid="audit-filter-clear" onClick={() => { setPage(0); setFilters({ user_email: "", entity_type: "all", action: "all", start: "", end: "" }); }}>
          <Trash2 size={13} /> Temizle
        </button>
      </div>
    </section>

    <section className="panel full-panel">
      <div className="table-scroll"><table>
        <thead><tr>
          <th style={{ width: 24 }}></th>
          <th>TARİH / SAAT</th><th>KULLANICI</th><th>İŞLEM</th><th>KAYIT TİPİ</th><th>KAYIT ID</th><th>HTTP</th><th>IP</th>
        </tr></thead>
        <tbody>
          {data.rows.length === 0 && <tr><td colSpan="8" style={{ textAlign: "center", padding: 24 }} className="muted">Seçilen filtrelerle kayıt bulunamadı.</td></tr>}
          {data.rows.map(r => {
            const meta = ACTION_META[r.action] || { label: r.action, color: "#6b7280", bg: "#f3f4f6" };
            const open = !!expanded[r.id];
            return <Fragment key={r.id}>
              <tr data-testid={`audit-row-${r.id}`} onClick={() => toggle(r.id)} style={{ cursor: "pointer" }}>
                <td>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                <td>{new Date(r.ts).toLocaleString("tr-TR")}</td>
                <td><strong>{r.user_email}</strong>{r.user_role && <span>{r.user_role}</span>}</td>
                <td><span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, background: meta.bg, color: meta.color, fontWeight: 600 }}>{meta.label}</span></td>
                <td>{ENTITY_LABEL[r.entity_type] || r.entity_type || "—"}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{(r.entity_id || "").slice(0, 12)}{r.entity_id && r.entity_id.length > 12 ? "..." : ""}</td>
                <td><span style={{ color: r.status >= 400 ? "#dc2626" : "#16a34a", fontWeight: 500 }}>{r.status}</span></td>
                <td style={{ fontFamily: "monospace", fontSize: 11, color: "#6b7280" }}>{r.ip || "—"}</td>
              </tr>
              {open && <tr data-testid={`audit-detail-${r.id}`}>
                <td colSpan="8" style={{ background: "#f8fafc", padding: 12 }}>
                  <div style={{ fontSize: 12, color: "#374151" }}>
                    <div><strong>Yol:</strong> <code>{r.method} {r.path}</code></div>
                    {r.body && <div style={{ marginTop: 6 }}>
                      <strong>İstek gövdesi:</strong>
                      <pre style={{ background: "white", padding: 8, borderRadius: 6, overflow: "auto", maxHeight: 200, marginTop: 4, fontSize: 11 }}>{JSON.stringify(r.body, null, 2)}</pre>
                    </div>}
                  </div>
                </td>
              </tr>}
            </Fragment>;
          })}
        </tbody>
      </table></div>
      {totalPages > 1 && <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 12, borderTop: "1px solid #e5e7eb" }}>
        <span className="muted" style={{ fontSize: 13 }}>Sayfa {page + 1} / {totalPages}</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="outline-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev">← Önceki</button>
          <button className="outline-btn" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="audit-next">Sonraki →</button>
        </div>
      </div>}
    </section>
  </div>;
}
